/**
 * The portfolio API modules: taxonomies, projects, contracts, contractor dossiers,
 * and the date-bounded activity feed (ADR-025 read models, Phase 1 contract).
 *
 * Author: Mahmoud Ragab Ahmed.
 *
 * Every function states the shape it was promised, field by field, and decides nothing
 * about what this principal may see: absence answers as absence all the way down.
 */

import { apiJson, listBody, num, optStr, str, strArray, query } from "./client";

/* ---------------------------------------------------------------------- */
/* Taxonomies: one call, canonical tokens plus resolved display labels.    */
/* ---------------------------------------------------------------------- */

export interface TaxonomyToken {
  readonly token: string;
  readonly label: string;
  /**
   * The register row's own id, when the register is a table-backed one that carries it
   * (stages always; disciplines since M5 P3b, because ItpCreate demands a discipline_id
   * UUID). Enum-backed value sets have no row and never carry one.
   */
  readonly id?: string;
  /** Party types only: true for the operator's own organisation types. */
  readonly isInternal?: boolean;
}

/**
 * One ITP intervention level (wave R0, the item editor): its canonical token, the
 * tenant's label, the notation the ITP sheet prints (H, W, S, S/R, R) and whether the
 * level requires the operator to attend, which is what the submit guard reads.
 */
export interface InterventionLevel {
  readonly token: string;
  readonly label: string;
  readonly notation: string;
  readonly requiresOperatorAttendance: boolean;
}

export interface Taxonomies {
  readonly locale: string;
  readonly stages: readonly TaxonomyToken[];
  readonly disciplines: readonly TaxonomyToken[];
  readonly verificationTypes: readonly TaxonomyToken[];
  readonly findingClasses: readonly TaxonomyToken[];
  readonly actionTypes: readonly TaxonomyToken[];
  readonly partyTypes: readonly TaxonomyToken[];
  readonly contractStatuses: readonly TaxonomyToken[];
  /** Wave R0: the ITP intervention levels, in the register's sequence. */
  readonly interventionLevels: readonly InterventionLevel[];
  /** Wave Experience A: the period kinds a blocked period is declared with. */
  readonly calendarPeriodKinds?: readonly TaxonomyToken[];
  /** Wave RFI ruling 3: ENGINEERING_ONLY or ANY, for a request without an ITP item. */
  readonly freeRfiScope?: string;
}

function interventionLevels(value: unknown): InterventionLevel[] {
  // The server always sends the register; an older stub without it reads as empty, and
  // the item editor then says the levels could not be read instead of offering none.
  if (value === undefined) {
    return [];
  }
  if (!Array.isArray(value)) {
    throw new Error("Expected the intervention level register");
  }
  return value.map((entry) => {
    if (typeof entry !== "object" || entry === null) {
      throw new Error("Expected an intervention level");
    }
    const record = entry as Record<string, unknown>;
    return {
      token: str(record.token),
      label: str(record.label),
      notation: str(record.notation),
      requiresOperatorAttendance: record.requires_operator_attendance === true,
    };
  });
}

interface RawTokenList {
  readonly token: string;
  readonly label: string;
  readonly id?: string;
  readonly isInternal?: boolean;
}

function tokens(value: unknown): TaxonomyToken[] {
  if (!Array.isArray(value)) {
    throw new Error("Expected a taxonomy token register");
  }
  return value.map((entry) => {
    if (typeof entry !== "object" || entry === null) {
      throw new Error("Expected a taxonomy token");
    }
    const record = entry as Record<string, unknown>;
    return {
      token: str(record.token),
      label: str(record.label),
      ...(record.id !== undefined ? { id: str(record.id) } : {}),
      ...(typeof record.is_internal === "boolean" ? { isInternal: record.is_internal } : {}),
    } satisfies RawTokenList;
  });
}

export async function getTaxonomies(token: string, signal?: AbortSignal): Promise<Taxonomies> {
  return apiJson(
    token,
    "/taxonomies",
    (body) => {
      if (typeof body !== "object" || body === null) {
        throw new Error("Expected the taxonomies body");
      }
      const record = body as Record<string, unknown>;
      return {
        locale: str(record.locale),
        stages: tokens(record.stages),
        disciplines: tokens(record.disciplines),
        verificationTypes: tokens(record.verification_types),
        findingClasses: tokens(record.finding_classes),
        actionTypes: tokens(record.action_types),
        partyTypes: tokens(record.party_types),
        contractStatuses: tokens(record.contract_statuses),
        interventionLevels: interventionLevels(record.intervention_levels),
        calendarPeriodKinds:
          record.calendar_period_kinds === undefined ? [] : tokens(record.calendar_period_kinds),
        ...(typeof record.free_rfi_scope === "string"
          ? { freeRfiScope: record.free_rfi_scope }
          : {}),
      };
    },
    { signal },
  );
}

/* ---------------------------------------------------------------------- */
/* Projects.                                                               */
/* ---------------------------------------------------------------------- */

export interface ProjectSummary {
  readonly id: string;
  readonly humanId: string;
  readonly projectNumber: string;
  readonly name: string;
  readonly category: string | null;
  readonly location: string | null;
  readonly costCentre: string | null;
  readonly startDate: string | null;
  readonly endDate: string | null;
  readonly completionDate: string | null;
  readonly parentProjectId: string | null;
  readonly leadQaqcDisplayName: string | null;
  readonly disciplineIds: readonly string[];
  /** PENDING_APPROVAL, PENDING_ASSIGNMENT or REGISTERED (amendment 1); null on older rows. */
  readonly status: string | null;
}

function project(body: Record<string, unknown>): ProjectSummary {
  return {
    id: str(body.id),
    humanId: str(body.human_id),
    projectNumber: str(body.project_number),
    name: str(body.name),
    category: optStr(body.category),
    location: optStr(body.location),
    costCentre: optStr(body.cost_centre),
    startDate: optStr(body.start_date),
    endDate: optStr(body.end_date),
    completionDate: optStr(body.completion_date),
    parentProjectId: optStr(body.parent_project_id),
    leadQaqcDisplayName: optStr(body.lead_qaqc_display_name),
    disciplineIds: strArray(body.discipline_ids ?? []),
    status: optStr(body.status),
  };
}

export async function listProjects(
  token: string,
  filters: { parentId?: string | null; signal?: AbortSignal } = {},
): Promise<{ total: number; items: ProjectSummary[]; startCodes: readonly string[] }> {
  const body = await apiJson(
    token,
    `/projects${query({ parent_id: filters.parentId })}`,
    (raw) => {
      const list = listBody(raw);
      const record = raw as Record<string, unknown>;
      const actions = Array.isArray(record.next_actions) ? record.next_actions : [];
      // Wave W1.5: what the caller may START from the register (REGISTER_PROJECT,
      // REGISTER_PARTY), so no form is offered that its submit would refuse.
      const startCodes = actions.flatMap((one: unknown) =>
        typeof one === "object" &&
        one !== null &&
        typeof (one as { code?: unknown }).code === "string"
          ? [(one as { code: string }).code]
          : [],
      );
      return { ...list, startCodes };
    },
    {
      signal: filters.signal,
    },
  );
  return { total: body.total, items: body.items.map(project), startCodes: body.startCodes };
}

export interface NextActionOut {
  readonly code: string;
  readonly method: string;
  readonly href: string;
  readonly target_type?: string | null;
  readonly target_id?: string | null;
  /**
   * Wave Experience A: true for an act the caller may take, false for a link that only
   * navigates (VOCABULARY.yaml next_action flag NAVIGATES_ONLY). The server's word.
   */
  readonly actionable_by_me?: boolean;
}

export interface TeamMember {
  /** The assignment row: what an explicit removal names (wave W1.5). */
  readonly id?: string;
  readonly personId: string;
  readonly roleId: string;
  readonly roleCode: string;
  readonly roleName: string;
  readonly displayName: string;
  readonly scope?: string;
  readonly scopeContractId?: string | null;
  /** Who put this person on the team, and when (wave W1.5, finding 5). */
  readonly assignedAt?: string | null;
  readonly assignedByDisplayName?: string | null;
  /**
   * Wave INST ruling 5: the discipline the assignment is narrowed to; null grants every
   * discipline (multi-discipline, the behaviour before the wave).
   */
  readonly disciplineId?: string | null;
}

export interface ProjectDetail {
  readonly id: string;
  readonly humanId: string;
  readonly projectNumber: string;
  readonly name: string;
  readonly description?: string | null;
  readonly category: string | null;
  readonly location: string | null;
  readonly costCentre: string | null;
  readonly startDate: string | null;
  readonly endDate: string | null;
  readonly completionDate: string | null;
  readonly parentProjectId: string | null;
  readonly leadQaqcPersonId: string | null;
  readonly leadQaqcDisplayName: string | null;
  readonly registeredAt?: string | undefined;
  readonly registeredBy?: string | null;
  readonly registeredByDisplayName: string | null;
  readonly approvedAt: string | null;
  readonly approvedBy: string | null;
  readonly approvedByDisplayName: string | null;
  readonly updatedAt?: string | undefined;
  readonly updatedBy?: string | null;
  readonly updatedByDisplayName: string | null;
  readonly status: string | null;
  readonly disciplineIds: readonly string[];
  readonly team: readonly TeamMember[];
  readonly nextActions: readonly NextActionOut[];
  /**
   * The staffing truth (Mahmoud's rulings of 2026-10-11): a registered project with no
   * QA/QC engineer on the project or its main contract. Operator side only.
   */
  readonly qaqcEngineerRequired: boolean;
  /**
   * Wave S: the caller may revoke a portal account's contracts on this project's bible
   * (the verb holder, or this project's approver). A server fact: the client never
   * compares its own id with `approvedBy`.
   */
  readonly canRevokePortalContracts: boolean;
  /**
   * Wave Experience A: the caller may assign this project's contracts to portal accounts
   * (PARTY CREATE_PORTAL_USER; an approver may revoke but not assign). A server fact.
   */
  readonly canAssignPortalContracts: boolean;
}

function parseNextAction(raw: unknown): NextActionOut {
  if (typeof raw !== "object" || raw === null) {
    throw new Error("Expected next action record");
  }
  const record = raw as Record<string, unknown>;
  return {
    code: str(record.code),
    method: str(record.method),
    href: str(record.href),
    target_type: optStr(record.target_type),
    target_id: optStr(record.target_id),
    // Wave Experience A: the server's word; absent on an older server, which reads as an act.
    actionable_by_me: record.actionable_by_me !== false,
  };
}

function parseTeamMember(raw: unknown): TeamMember {
  if (typeof raw !== "object" || raw === null) {
    throw new Error("Expected team member record");
  }
  const record = raw as Record<string, unknown>;
  return {
    ...(typeof record.id === "string" ? { id: record.id } : {}),
    personId: str(record.person_id),
    roleId: str(record.role_id),
    roleCode: str(record.role_code),
    // The schema names it role_label; role_name is read as a fallback for older bodies.
    roleName: str(record.role_label ?? record.role_name),
    displayName: str(record.display_name),
    ...(typeof record.scope === "string" ? { scope: record.scope } : {}),
    scopeContractId: optStr(record.scope_contract_id),
    assignedAt: optStr(record.assigned_at),
    assignedByDisplayName: optStr(record.assigned_by_display_name),
    disciplineId: optStr(record.discipline_id),
  };
}

function parseProjectDetail(body: Record<string, unknown>): ProjectDetail {
  const rawActions = Array.isArray(body.next_actions) ? body.next_actions : [];
  const rawTeam = Array.isArray(body.team) ? body.team : [];
  return {
    id: str(body.id),
    humanId: str(body.human_id),
    projectNumber: str(body.project_number),
    name: str(body.name),
    description: optStr(body.description),
    category: optStr(body.category),
    location: optStr(body.location),
    costCentre: optStr(body.cost_centre),
    startDate: optStr(body.start_date),
    endDate: optStr(body.end_date),
    completionDate: optStr(body.completion_date),
    parentProjectId: optStr(body.parent_project_id),
    leadQaqcPersonId: optStr(body.lead_qaqc_person_id),
    leadQaqcDisplayName: optStr(body.lead_qaqc_display_name),
    registeredAt: optStr(body.registered_at) ?? undefined,
    registeredBy: optStr(body.registered_by),
    registeredByDisplayName: optStr(body.registered_by_display_name),
    approvedAt: optStr(body.approved_at),
    approvedBy: optStr(body.approved_by),
    approvedByDisplayName: optStr(body.approved_by_display_name),
    updatedAt: optStr(body.updated_at) ?? undefined,
    updatedBy: optStr(body.updated_by),
    updatedByDisplayName: optStr(body.updated_by_display_name),
    status: optStr(body.status),
    disciplineIds: strArray(body.discipline_ids ?? []),
    team: rawTeam.map(parseTeamMember),
    nextActions: rawActions.map(parseNextAction),
    qaqcEngineerRequired: body.qaqc_engineer_required === true,
    canRevokePortalContracts: body.can_revoke_portal_contracts === true,
    canAssignPortalContracts: body.can_assign_portal_contracts === true,
  };
}

export async function getProject(
  token: string,
  projectId: string,
  signal?: AbortSignal,
): Promise<ProjectDetail> {
  return apiJson(
    token,
    `/projects/${projectId}`,
    (raw) => {
      if (typeof raw !== "object" || raw === null) {
        throw new Error("Expected a project detail record");
      }
      return parseProjectDetail(raw as Record<string, unknown>);
    },
    { signal },
  );
}

/* ---------------------------------------------------------------------- */
/* Contracts.                                                              */
/* ---------------------------------------------------------------------- */

export interface ContractSummary {
  readonly id: string;
  readonly humanId: string;
  readonly contractorPartyId: string;
  readonly contractorPartyName: string;
  readonly status: string;
  readonly statusLabel: string;
  readonly startDate: string | null;
  readonly endDate: string | null;
  readonly parentContractId: string | null;
  readonly linkedProjectIds: readonly string[];
  readonly disciplineIds: readonly string[];
  /** The project's MAIN contract (wave W0): one per project. */
  readonly isMain: boolean;
  /**
   * Wave W2: how many of the contractor's people can sign in. Zero means its tasks wait
   * for nobody. Null when the server did not say (an older body): no notice is drawn.
   */
  readonly contractorPortalUsers: number | null;
}

/** A count the server may or may not have sent; null when it did not. */
function optCount(value: unknown): number | null {
  return typeof value === "number" ? value : null;
}

function contract(body: Record<string, unknown>): ContractSummary {
  const linked = body.linked_project_ids;
  if (!Array.isArray(linked)) {
    throw new Error("Expected the linked project ids");
  }
  return {
    id: str(body.id),
    humanId: str(body.human_id),
    contractorPartyId: str(body.contractor_party_id),
    contractorPartyName: str(body.contractor_party_name),
    status: str(body.status),
    statusLabel: str(body.status_label),
    startDate: optStr(body.start_date),
    endDate: optStr(body.end_date),
    parentContractId: optStr(body.parent_contract_id),
    linkedProjectIds: linked.map(str),
    disciplineIds: strArray(body.discipline_ids ?? []),
    isMain: body.is_main === true,
    contractorPortalUsers: optCount(body.contractor_portal_users),
  };
}

/**
 * The contract register. `mainOnly`: projects' MAIN contracts only (the contractors'
 * register, wave W0). `itpAuthoring`: exactly the contracts the caller may register a new
 * plan on, the same statement the server's create check asks.
 */
export async function listContracts(
  token: string,
  filters: {
    projectId?: string | null;
    mainOnly?: boolean;
    itpAuthoring?: boolean;
    signal?: AbortSignal;
  } = {},
): Promise<{ total: number; items: ContractSummary[] }> {
  const body = await apiJson(
    token,
    `/contracts${query({
      project_id: filters.projectId,
      main_only: filters.mainOnly === true ? true : undefined,
      itp_authoring: filters.itpAuthoring === true ? true : undefined,
    })}`,
    listBody,
    { signal: filters.signal },
  );
  return { total: body.total, items: body.items.map(contract) };
}

export interface ContractDetail {
  readonly id: string;
  readonly humanId: string;
  readonly title: string | null;
  readonly scopeDescription: string | null;
  readonly contractorPartyId: string;
  readonly contractorPartyName: string;
  readonly status: string;
  readonly statusLabel: string;
  readonly startDate: string | null;
  readonly endDate: string | null;
  readonly valueAmount: string | null;
  readonly valueCurrency: string | null;
  readonly parentContractId: string | null;
  readonly linkedProjectIds: readonly string[];
  readonly disciplineIds: readonly string[];
  readonly isMain: boolean;
  /** Wave W2: see ContractSummary.contractorPortalUsers. */
  readonly contractorPortalUsers: number | null;
  /** Wave W2: the contract's team, as the project page's (operator-internal). */
  readonly team: readonly TeamMember[];
  readonly nextActions: readonly NextActionOut[];
}

function parseContractDetail(body: Record<string, unknown>): ContractDetail {
  const linked = Array.isArray(body.linked_project_ids) ? body.linked_project_ids : [];
  const rawActions = Array.isArray(body.next_actions) ? body.next_actions : [];
  const rawTeam = Array.isArray(body.team) ? body.team : [];
  return {
    id: str(body.id),
    humanId: str(body.human_id),
    title: optStr(body.title),
    scopeDescription: optStr(body.scope_description),
    contractorPartyId: str(body.contractor_party_id),
    contractorPartyName: str(body.contractor_party_name),
    status: str(body.status),
    statusLabel: str(body.status_label),
    startDate: optStr(body.start_date),
    endDate: optStr(body.end_date),
    valueAmount: optStr(body.value_amount),
    valueCurrency: optStr(body.value_currency),
    parentContractId: optStr(body.parent_contract_id),
    linkedProjectIds: linked.map(str),
    disciplineIds: strArray(body.discipline_ids ?? []),
    isMain: body.is_main === true,
    contractorPortalUsers: optCount(body.contractor_portal_users),
    team: rawTeam.map(parseTeamMember),
    nextActions: rawActions.map(parseNextAction),
  };
}

export async function getContract(
  token: string,
  contractId: string,
  signal?: AbortSignal,
): Promise<ContractDetail> {
  return apiJson(
    token,
    `/contracts/${contractId}`,
    (raw) => {
      if (typeof raw !== "object" || raw === null) {
        throw new Error("Expected a contract detail record");
      }
      return parseContractDetail(raw as Record<string, unknown>);
    },
    { signal },
  );
}

/* ---------------------------------------------------------------------- */
/* Parties: the main-contractor choice of project registration (W0).       */
/* ---------------------------------------------------------------------- */

export interface PartySummary {
  readonly id: string;
  readonly name: string;
  readonly partyTypeCode: string;
  readonly status: string;
}

export async function listParties(token: string, signal?: AbortSignal): Promise<PartySummary[]> {
  const body = await apiJson(
    token,
    "/parties",
    (raw) => {
      const envelope = recordOf(raw);
      if (!Array.isArray(envelope.items)) {
        throw new Error("Expected the party list");
      }
      return envelope.items.map((one) => {
        const row = recordOf(one);
        return {
          id: str(row.id),
          name: str(row.name),
          partyTypeCode: str(row.party_type_code),
          status: str(row.status),
        };
      });
    },
    { signal },
  );
  return body;
}

/* ---------------------------------------------------------------------- */
/* The front door (M5 P4, M6 amendment 1): create project with its contract. */
/* ---------------------------------------------------------------------- */

/** One JSON object body, narrowed enough to field-read it. */
function recordOf(body: unknown): Record<string, unknown> {
  if (typeof body !== "object" || body === null) {
    throw new Error("Expected a JSON object");
  }
  return body as Record<string, unknown>;
}

/** A contractor not yet in the register, registered in the same act (amendment 1, item 1). */
export interface NewContractorPayload {
  readonly name: string;
  readonly partyTypeId: string;
  readonly registrationNo?: string | null;
  readonly country?: string | null;
  readonly contactNote?: string | null;
}

/**
 * The main contract, entered as an already-awarded fact (amendment 1: awarding is outside
 * Qualify). Exactly one of `contractorPartyId` and `newContractor` is set.
 */
export interface MainContractPayload {
  readonly title: string;
  readonly contractorPartyId?: string | null;
  readonly newContractor?: NewContractorPayload | null;
  readonly scopeDescription?: string | null;
  readonly disciplineIds: readonly string[];
  readonly startDate?: string | null;
  readonly endDate?: string | null;
  readonly valueAmount?: string | null;
  readonly valueCurrency?: string | null;
}

/** What an account holding PROJECT CREATE supplies: the project AND its awarded contract. */
export interface ProjectCreatePayload {
  readonly name: string;
  readonly projectNumber: string;
  readonly description?: string | null;
  readonly costCentre?: string | null;
  readonly category?: string | null;
  readonly location?: string | null;
  readonly parentProjectId?: string | null;
  readonly disciplineIds: readonly string[];
  readonly stageIds: readonly string[];
  readonly startDate?: string | null;
  readonly endDate?: string | null;
  readonly contract: MainContractPayload;
}

export interface ProjectCreated {
  readonly id: string;
  readonly humanId: string;
  readonly name: string;
  readonly status: string | null;
  readonly contractId: string | null;
  readonly contractHumanId: string | null;
  readonly contractorRegistered: boolean;
}

/** The counterparty must EXIST; nothing here creates one. The scope is the project's subset. */
export async function createProject(
  token: string,
  payload: ProjectCreatePayload,
): Promise<ProjectCreated> {
  return apiJson(
    token,
    `/projects`,
    (raw) => {
      const body = recordOf(raw);
      return {
        id: str(body.id),
        humanId: str(body.human_id),
        name: str(body.name),
        status: optStr(body.status),
        contractId: optStr(body.contract_id),
        contractHumanId: optStr(body.contract_human_id),
        contractorRegistered: body.contractor_registered === true,
      };
    },
    {
      method: "POST",
      body: {
        name: payload.name,
        project_number: payload.projectNumber,
        description: payload.description ?? null,
        cost_centre: payload.costCentre ?? null,
        category: payload.category ?? null,
        location: payload.location ?? null,
        parent_project_id: payload.parentProjectId ?? null,
        discipline_ids: payload.disciplineIds,
        stage_ids: payload.stageIds,
        start_date: payload.startDate ?? null,
        end_date: payload.endDate ?? null,
        contract: mainContractBody(payload.contract),
      },
    },
  );
}

function mainContractBody(contract: MainContractPayload): Record<string, unknown> {
  const fresh = contract.newContractor ?? null;
  return {
    title: contract.title,
    contractor_party_id: fresh === null ? (contract.contractorPartyId ?? null) : null,
    new_contractor:
      fresh === null
        ? null
        : {
            name: fresh.name,
            party_type_id: fresh.partyTypeId,
            registration_no: fresh.registrationNo ?? null,
            country: fresh.country ?? null,
            contact_note: fresh.contactNote ?? null,
          },
    scope_description: contract.scopeDescription ?? null,
    discipline_ids: contract.disciplineIds,
    start_date: contract.startDate ?? null,
    end_date: contract.endDate ?? null,
    value_amount: contract.valueAmount ?? null,
    value_currency: contract.valueCurrency ?? null,
  };
}

/* ---------------------------------------------------------------------- */
/* Amendment 1: the approval act, personnel assignment, the provenance.    */
/* ---------------------------------------------------------------------- */

export interface AssignPayload {
  readonly personId: string;
  readonly roleId: string;
}

export interface ProjectApproved {
  readonly id: string;
  readonly status: string;
  readonly vendorDisciplines: readonly string[];
  readonly assignmentId: string | null;
  readonly itpsRequested: boolean;
}

/**
 * POST /api/projects/{id}/approve. `approveVendor` is the explicit D-05 vendor step;
 * `team` (wave W1.5) is every person named in the same sitting, all of them added.
 */
export async function approveProject(
  token: string,
  projectId: string,
  options: {
    approveVendor: boolean;
    assign?: AssignPayload | null;
    team?: readonly AssignPayload[];
  },
): Promise<ProjectApproved> {
  return apiJson(
    token,
    `/projects/${projectId}/approve`,
    (raw) => {
      const body = recordOf(raw);
      return {
        id: str(body.id),
        status: str(body.status),
        vendorDisciplines: strArray(body.vendor_disciplines ?? []),
        assignmentId: optStr(body.assignment_id),
        itpsRequested: body.itps_requested === true,
      };
    },
    {
      method: "POST",
      body: {
        approve_vendor: options.approveVendor,
        assign:
          options.assign === undefined || options.assign === null
            ? null
            : { person_id: options.assign.personId, role_id: options.assign.roleId },
        assignments: (options.team ?? []).map((one) => ({
          person_id: one.personId,
          role_id: one.roleId,
        })),
      },
    },
  );
}

export interface AssignmentCreated {
  readonly id: string;
  readonly scope: string;
  readonly itpsRequested: boolean;
}

/**
 * POST /api/role-assignments: one operator person, one role, a project or a contract.
 * Wave INST ruling 5: optionally one discipline (null: every discipline); a discipline
 * the tenant does not hold is 409 assignment.discipline_unknown.
 */
export async function createRoleAssignment(
  token: string,
  payload: AssignPayload & {
    projectId?: string | null;
    contractId?: string | null;
    disciplineId?: string | null;
  },
): Promise<AssignmentCreated> {
  return apiJson(
    token,
    "/role-assignments",
    (raw) => {
      const body = recordOf(raw);
      return {
        id: str(body.id),
        scope: str(body.scope),
        itpsRequested: body.itps_requested === true,
      };
    },
    {
      method: "POST",
      body: {
        person_id: payload.personId,
        role_id: payload.roleId,
        project_id: payload.projectId ?? null,
        contract_id: payload.contractId ?? null,
        discipline_id: payload.disciplineId ?? null,
      },
    },
  );
}

/** DELETE /api/role-assignments/{id}: an explicit removal, audited with the whole row. */
export async function revokeRoleAssignment(token: string, assignmentId: string): Promise<void> {
  await apiJson(token, `/role-assignments/${assignmentId}`, () => undefined, {
    method: "DELETE",
  });
}

/* ---------------------------------------------------------------------- */
/* The approval's outlook (wave W1.5, finding 4): read BEFORE the attempt. */
/* ---------------------------------------------------------------------- */

export interface VendorGapOut {
  readonly partyId: string;
  readonly partyName: string;
  readonly disciplineId: string;
  readonly disciplineLabel: string;
}

export interface VendorApprover {
  /** Null: every account of the operator's organisation holds it. */
  readonly roleLabel: string | null;
  readonly byGrant: boolean;
}

export interface ApprovalOutlook {
  readonly vendorGaps: readonly VendorGapOut[];
  readonly mayApproveVendor: boolean;
  readonly vendorSelfRegistered: boolean;
  readonly vendorApprovers: readonly VendorApprover[];
  readonly ableToCompleteRoles: readonly string[];
  /**
   * Mahmoud's ruling (2026-10-11): the approval cannot complete until a QA/QC engineer is
   * on the main contract. True when the team has none yet: the dialog requires one.
   */
  readonly qaqcEngineerRequired: boolean;
  readonly mainContractHumanId: string | null;
  readonly qaqcRole: { readonly id: string; readonly label: string } | null;
}

/** GET /api/projects/{id}/approval-outlook. Absence for anyone who may not approve. */
export async function getApprovalOutlook(
  token: string,
  projectId: string,
  signal?: AbortSignal,
): Promise<ApprovalOutlook> {
  return apiJson(
    token,
    `/projects/${projectId}/approval-outlook`,
    (raw) => {
      const body = recordOf(raw);
      const gaps = Array.isArray(body.vendor_gaps) ? body.vendor_gaps : [];
      const approvers = Array.isArray(body.vendor_approvers) ? body.vendor_approvers : [];
      return {
        vendorGaps: gaps.map((one) => {
          const gap = recordOf(one);
          return {
            partyId: str(gap.party_id),
            partyName: str(gap.party_name),
            disciplineId: str(gap.discipline_id),
            disciplineLabel: str(gap.discipline_label),
          };
        }),
        mayApproveVendor: body.may_approve_vendor === true,
        vendorSelfRegistered: body.vendor_self_registered === true,
        vendorApprovers: approvers.map((one) => {
          const approver = recordOf(one);
          return { roleLabel: optStr(approver.role_label), byGrant: approver.by_grant === true };
        }),
        ableToCompleteRoles: strArray(body.able_to_complete_roles ?? []),
        qaqcEngineerRequired: body.qaqc_engineer_required === true,
        mainContractHumanId: optStr(body.main_contract_human_id),
        qaqcRole:
          typeof body.qaqc_role_id === "string" && typeof body.qaqc_role_label === "string"
            ? { id: body.qaqc_role_id, label: body.qaqc_role_label }
            : null,
      };
    },
    { signal },
  );
}

export interface HistoryEntry {
  readonly seq: number;
  readonly at: string;
  readonly objectType: string;
  readonly event: string | null;
  readonly fromState: string | null;
  readonly toState: string | null;
  readonly actorDisplayName: string | null;
}

/** GET /api/projects/{id}/history: operator-internal; anyone else reads absence. */
export async function getProjectHistory(
  token: string,
  projectId: string,
  signal?: AbortSignal,
): Promise<HistoryEntry[]> {
  return apiJson(
    token,
    `/projects/${projectId}/history`,
    (raw) => {
      const body = recordOf(raw);
      if (!Array.isArray(body.items)) {
        throw new Error("Expected the history entries");
      }
      return body.items.map((one) => {
        const row = recordOf(one);
        return {
          seq: num(row.seq),
          at: str(row.at),
          objectType: str(row.object_type),
          event: optStr(row.event),
          fromState: optStr(row.from_state),
          toState: optStr(row.to_state),
          actorDisplayName: optStr(row.actor_display_name),
        };
      });
    },
    { signal },
  );
}

/* ---------------------------------------------------------------------- */
/* Contractor dossiers: percentages only, no ADR-014 band logic.           */
/* ---------------------------------------------------------------------- */

export interface ResponseTimeliness {
  readonly decided: number;
  readonly agreedOnTime: number;
  readonly rate: number | null;
}

export interface ContractorDossier {
  readonly partyId: string;
  readonly partyName: string;
  readonly inspectionsConducted: number;
  readonly firstTimePassRate: number | null;
  readonly findingsByClass: Readonly<Record<string, number>>;
  readonly openNcrCarAging: Readonly<Record<string, number>>;
  readonly meanDaysOpenClosed: number | null;
  readonly responseTimeliness: ResponseTimeliness;
  readonly overdueActions: number;
  /** Wave W2: people of this party who can sign in; null when the server did not say. */
  readonly portalUsers: number | null;
}

function counts(value: unknown): Record<string, number> {
  if (typeof value !== "object" || value === null) {
    throw new Error("Expected a count map");
  }
  const record = value as Record<string, unknown>;
  const out: Record<string, number> = {};
  for (const [key, one] of Object.entries(record)) {
    out[key] = num(one);
  }
  return out;
}

function dossier(body: Record<string, unknown>): ContractorDossier {
  const timeliness = body.response_timeliness;
  if (typeof timeliness !== "object" || timeliness === null) {
    throw new Error("Expected the response timeliness");
  }
  const record = timeliness as Record<string, unknown>;
  return {
    partyId: str(body.party_id),
    partyName: str(body.party_name),
    inspectionsConducted: num(body.inspections_conducted),
    firstTimePassRate: body.first_time_pass_rate === null ? null : num(body.first_time_pass_rate),
    findingsByClass: counts(body.findings_by_class),
    openNcrCarAging: counts(body.open_ncr_car_aging),
    meanDaysOpenClosed:
      body.mean_days_open_closed === null ? null : num(body.mean_days_open_closed),
    responseTimeliness: {
      decided: num(record.decided),
      agreedOnTime: num(record.agreed_on_time),
      rate: record.rate === null ? null : num(record.rate),
    },
    overdueActions: num(body.overdue_actions),
    portalUsers: optCount(body.portal_users),
  };
}

export async function listContractorDossiers(
  token: string,
  signal?: AbortSignal,
): Promise<ContractorDossier[]> {
  return apiJson(
    token,
    "/analytics/contractors",
    (body) => {
      if (!Array.isArray(body)) {
        throw new Error("Expected the dossier list");
      }
      return body.map((entry) => {
        if (typeof entry !== "object" || entry === null) {
          throw new Error("Expected a dossier row");
        }
        return dossier(entry as Record<string, unknown>);
      });
    },
    { signal },
  );
}

/* ---------------------------------------------------------------------- */
/* The activity feed (the calendar's read model, date-bounded).            */
/* ---------------------------------------------------------------------- */

export interface ActivityFeedRow {
  readonly id: string;
  readonly humanId: string;
  readonly scheduleId: string;
  readonly plannedDate: string;
  readonly status: string;
  readonly statusLabel: string;
  readonly priority: string | null;
  readonly purpose: string | null;
  readonly contractorPartyName: string;
  readonly verificationTypeCode: string;
  readonly verificationTypeLabel: string;
  readonly disciplineCode: string | null;
  readonly disciplineLabel: string | null;
}

function activityRow(body: Record<string, unknown>): ActivityFeedRow {
  return {
    id: str(body.id),
    humanId: str(body.human_id),
    scheduleId: str(body.schedule_id),
    plannedDate: str(body.planned_date),
    status: str(body.status),
    statusLabel: str(body.status_label),
    priority: optStr(body.priority),
    purpose: optStr(body.purpose),
    contractorPartyName: str(body.contractor_party_name),
    verificationTypeCode: str(body.verification_type_code),
    verificationTypeLabel: str(body.verification_type_label),
    disciplineCode: body.discipline_code == null ? null : str(body.discipline_code),
    disciplineLabel: body.discipline_label == null ? null : str(body.discipline_label),
  };
}

export async function listActivityFeed(
  token: string,
  filters: { fromDate?: string | null; toDate?: string | null; signal?: AbortSignal } = {},
): Promise<{ total: number; items: ActivityFeedRow[] }> {
  const body = await apiJson(
    token,
    `/activities${query({ from_date: filters.fromDate, to_date: filters.toDate })}`,
    listBody,
    { signal: filters.signal },
  );
  return { total: body.total, items: body.items.map(activityRow) };
}

/* ---------------------------------------------------------------------- */
/* Contractor portal accounts (wave O): create, list, assign, revoke.      */
/* ---------------------------------------------------------------------- */

/** How the temporary password reaches the person: on this screen, or by email. */
export type PasswordHandover = "SCREEN" | "EMAIL";

/** The account the server just made. The temporary password is shown once, never kept. */
export interface PortalUserCreated {
  readonly personId: string;
  readonly partyId: string;
  readonly username: string;
  readonly email: string;
  readonly fullName: string;
  /** Null when a real email transport sent it to the person instead (handover EMAIL). */
  readonly temporaryPassword: string | null;
  readonly handover: PasswordHandover;
}

export interface AssignedContract {
  readonly id: string;
  readonly humanId: string;
  readonly title: string | null;
  /** Wave Experience A: the caller may revoke this assignment (the DELETE's own rule). */
  readonly revocableByMe?: boolean;
}

/** Wave Experience A: the accounts with the server's words for the affordances. */
export interface PortalAccounts {
  readonly accounts: readonly PortalUser[];
  /** Null on an older server that did not say. */
  readonly canAssign: boolean | null;
  readonly canRevoke: boolean | null;
}

/** One account connected to a contractor, with the contracts it may see. */
export interface PortalUser {
  readonly personId: string;
  readonly username: string;
  readonly name: string;
  readonly email: string;
  readonly assignedContracts: readonly AssignedContract[];
  readonly createdByName: string | null;
  readonly createdAt: string | null;
}

/** POST /api/parties/{party}/portal-users: the one place the temporary password travels. */
export async function createPortalUser(
  token: string,
  partyId: string,
  payload: { fullName: string; email: string; username: string },
): Promise<PortalUserCreated> {
  return apiJson(
    token,
    `/parties/${partyId}/portal-users`,
    (raw) => {
      const body = recordOf(raw);
      return {
        personId: str(body.person_id),
        partyId: str(body.party_id),
        username: str(body.username),
        email: str(body.email),
        fullName: str(body.full_name),
        // Wave COM item 4: with a real email transport the server answers null and
        // EMAIL; null stays null (str() would render the word "null" in the dialog).
        temporaryPassword:
          body.temporary_password === null || body.temporary_password === undefined
            ? null
            : str(body.temporary_password),
        // An older server says nothing: it always handed the password over on screen.
        handover: body.handover === "EMAIL" ? "EMAIL" : "SCREEN",
      };
    },
    {
      method: "POST",
      body: { full_name: payload.fullName, email: payload.email, username: payload.username },
    },
  );
}

/**
 * GET /api/parties/{party}/portal-users: the accounts connected to a contractor.
 *
 * Wave S: with `projectId` (the project bible) each account carries only its assignments
 * on that project's contracts, and an account with none there is omitted.
 */
export async function listPortalUsers(
  token: string,
  partyId: string,
  options: { readonly projectId?: string | null; readonly signal?: AbortSignal } = {},
): Promise<PortalUser[]> {
  return [...(await readPortalAccounts(token, partyId, options)).accounts];
}

export async function readPortalAccounts(
  token: string,
  partyId: string,
  options: { readonly projectId?: string | null; readonly signal?: AbortSignal } = {},
): Promise<PortalAccounts> {
  const raw = await apiJson(
    token,
    `/parties/${partyId}/portal-users${query({ project_id: options.projectId })}`,
    (value) => value,
    { signal: options.signal },
  );
  const body = listBody(raw);
  const words = recordOf(raw);
  const accounts = body.items.map((raw) => {
    const contracts = Array.isArray(raw.assigned_contracts) ? raw.assigned_contracts : [];
    return {
      personId: str(raw.person_id),
      username: str(raw.username),
      name: str(raw.name),
      email: str(raw.email),
      assignedContracts: contracts.map((one: unknown) => {
        const contract = recordOf(one);
        return {
          id: str(contract.id),
          humanId: str(contract.human_id),
          title: optStr(contract.title),
          ...(typeof contract.revocable_by_me === "boolean"
            ? { revocableByMe: contract.revocable_by_me }
            : {}),
        };
      }),
      createdByName: optStr(raw.created_by_name),
      createdAt: optStr(raw.created_at),
    };
  });
  return {
    accounts,
    canAssign:
      typeof words.can_assign_portal_contracts === "boolean"
        ? words.can_assign_portal_contracts
        : null,
    canRevoke:
      typeof words.can_revoke_portal_contracts === "boolean"
        ? words.can_revoke_portal_contracts
        : null,
  };
}

/**
 * POST /api/parties/{party}/portal-users/{person}/contracts: give the account sight of one
 * contract, or of every contract of one project.
 */
export async function assignPortalUserContracts(
  token: string,
  partyId: string,
  personId: string,
  target: { contractId: string } | { projectId: string },
): Promise<void> {
  await apiJson(token, `/parties/${partyId}/portal-users/${personId}/contracts`, () => undefined, {
    method: "POST",
    body:
      "contractId" in target
        ? { contract_id: target.contractId, project_id: null }
        : { contract_id: null, project_id: target.projectId },
  });
}

/** DELETE /api/parties/{party}/portal-users/{person}/contracts/{contract}: take sight away. */
export async function revokePortalUserContract(
  token: string,
  partyId: string,
  personId: string,
  contractId: string,
): Promise<void> {
  await apiJson(
    token,
    `/parties/${partyId}/portal-users/${personId}/contracts/${contractId}`,
    () => undefined,
    { method: "DELETE" },
  );
}
